import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  WhatsappLogo, PaperPlaneTilt, Plus, MagnifyingGlass, Gear, Paperclip, Check, Checks,
  Clock, Copy, Warning, FileText, ArrowClockwise, UserCircle, Article, ClipboardText,
  LinkSimple, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function StatusTicks({ status }) {
  if (status === "failed") return <Warning className="h-3.5 w-3.5 text-red-300" weight="fill" />;
  if (status === "read") return <Checks className="h-3.5 w-3.5 text-sky-300" weight="bold" />;
  if (status === "delivered") return <Checks className="h-3.5 w-3.5 text-white/70" weight="bold" />;
  if (status === "sent") return <Check className="h-3.5 w-3.5 text-white/70" weight="bold" />;
  return <Clock className="h-3.5 w-3.5 text-white/60" />;
}

function MediaImage({ mediaId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url;
    let live = true;
    API.get(`/whatsapp/media/${mediaId}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); if (live) setSrc(url); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [mediaId]);
  if (err) return <p className="text-xs italic opacity-70">Image no longer available (WhatsApp media expires after ~7 days)</p>;
  if (!src) return <div className="h-32 w-44 rounded bg-black/10 animate-pulse" />;
  return <img src={src} alt="WhatsApp attachment" className="max-h-64 max-w-full rounded" />;
}

function CopyRow({ label, value, testid }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input readOnly value={value} className="font-mono text-xs" data-testid={testid} />
        <Button type="button" size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); }} data-testid={`${testid}-copy`}>
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ==================== Settings / Setup dialog (admin) ==================== */
function SettingsDialog({ open, onOpenChange, onConnected }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ access_token: "", phone_number_id: "", waba_id: "", app_secret: "",
    auto_reply_enabled: false, auto_reply_message: "", hours_start: "09:00", hours_end: "17:30", work_days: [0, 1, 2, 3, 4],
    emergency_alerts_enabled: true, emergency_keywords: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    API.get("/whatsapp/settings").then(({ data }) => {
      setCfg(data);
      setForm({
        access_token: data.access_token_masked || "",
        phone_number_id: data.phone_number_id || "",
        waba_id: data.waba_id || "",
        app_secret: data.app_secret_masked || "",
        auto_reply_enabled: !!data.auto_reply_enabled,
        auto_reply_message: data.auto_reply_message || "",
        hours_start: data.hours_start || "09:00",
        hours_end: data.hours_end || "17:30",
        work_days: data.work_days || [0, 1, 2, 3, 4],
        emergency_alerts_enabled: data.emergency_alerts_enabled !== false,
        emergency_keywords: (data.emergency_keywords || []).join(", "),
      });
    }).catch(() => toast.error("Could not load WhatsApp settings"));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/whatsapp/settings", form);
      setCfg(data);
      toast.success("WhatsApp credentials saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await API.post("/whatsapp/test-connection");
      toast.success(`Connected to ${data.verified_name || "WhatsApp"} (${data.phone_display})`);
      onConnected?.();
      const res = await API.get("/whatsapp/settings");
      setCfg(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection test failed");
    } finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wa-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WhatsappLogo className="h-5 w-5 text-emerald-600" weight="fill" /> WhatsApp Business Setup</DialogTitle>
          <DialogDescription>Connect your WhatsApp Business account via the official Meta Cloud API.</DialogDescription>
        </DialogHeader>

        {cfg?.connected ? (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300" data-testid="wa-connected-banner">
            ✓ Connected as <strong>{cfg.verified_name || "WhatsApp Business"}</strong> ({cfg.phone_display})
          </div>
        ) : (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
            Not connected yet — follow the steps below, then press Test Connection.
          </div>
        )}

        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm space-y-1.5">
          <p className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">How to get your keys</p>
          <ol className="list-decimal ml-4 space-y-1 text-xs text-muted-foreground">
            <li>Go to <a className="text-emerald-600 underline" href="https://developers.facebook.com" target="_blank" rel="noreferrer">developers.facebook.com</a> → Create App → type <strong>Business</strong>.</li>
            <li>On the app dashboard, add the <strong>WhatsApp</strong> product.</li>
            <li>Under <strong>WhatsApp → API Setup</strong> copy the <strong>Phone Number ID</strong> and <strong>WhatsApp Business Account (WABA) ID</strong>.</li>
            <li>Create a <strong>permanent access token</strong>: Meta Business Settings → System Users → generate token with <em>whatsapp_business_messaging</em> + <em>whatsapp_business_management</em> permissions.</li>
            <li>Under <strong>App Settings → Basic</strong> copy the <strong>App Secret</strong> (used to verify webhooks).</li>
            <li>Paste all four below, <strong>Save</strong>, then press <strong>Test Connection</strong>.</li>
            <li>Under <strong>WhatsApp → Configuration → Webhook</strong>, paste the Callback URL and Verify Token shown below, then subscribe to the <strong>messages</strong> webhook field.</li>
          </ol>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Permanent Access Token</Label>
            <Input className="mt-1 font-mono text-xs" value={form.access_token}
                   onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                   placeholder="EAAG..." data-testid="wa-token-input" />
          </div>
          <div>
            <Label className="text-xs">Phone Number ID</Label>
            <Input className="mt-1 font-mono text-xs" value={form.phone_number_id}
                   onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                   placeholder="1234567890" data-testid="wa-phone-id-input" />
          </div>
          <div>
            <Label className="text-xs">WABA ID</Label>
            <Input className="mt-1 font-mono text-xs" value={form.waba_id}
                   onChange={(e) => setForm({ ...form, waba_id: e.target.value })}
                   placeholder="1029384756" data-testid="wa-waba-input" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">App Secret</Label>
            <Input className="mt-1 font-mono text-xs" value={form.app_secret}
                   onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
                   placeholder="abc123..." data-testid="wa-secret-input" />
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-3" data-testid="wa-autoreply-section">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Out-of-hours Auto-Reply</p>
              <p className="text-xs text-muted-foreground mt-0.5">Automatically reply to incoming messages outside business hours (max once per 8h per contact).</p>
            </div>
            <Switch checked={form.auto_reply_enabled} onCheckedChange={(v) => setForm({ ...form, auto_reply_enabled: v })} data-testid="wa-autoreply-switch" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Business hours start</Label>
              <Input type="time" className="mt-1" value={form.hours_start}
                     onChange={(e) => setForm({ ...form, hours_start: e.target.value })} data-testid="wa-hours-start" />
            </div>
            <div>
              <Label className="text-xs">Business hours end</Label>
              <Input type="time" className="mt-1" value={form.hours_end}
                     onChange={(e) => setForm({ ...form, hours_end: e.target.value })} data-testid="wa-hours-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Working days</Label>
            <div className="flex gap-1.5 mt-1">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                <button key={d} type="button" data-testid={`wa-day-${d.toLowerCase()}`}
                        onClick={() => setForm({ ...form, work_days: form.work_days.includes(i) ? form.work_days.filter((x) => x !== i) : [...form.work_days, i].sort() })}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${form.work_days.includes(i) ? "bg-emerald-600 text-white border-emerald-600" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Auto-reply message</Label>
            <Textarea className="mt-1" rows={3} value={form.auto_reply_message}
                      onChange={(e) => setForm({ ...form, auto_reply_message: e.target.value })}
                      placeholder="Thanks for your message! Our office is currently closed…" data-testid="wa-autoreply-message" />
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-3" data-testid="wa-emergency-section">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Emergency Keyword Alerts</p>
              <p className="text-xs text-muted-foreground mt-0.5">Incoming messages containing these words flag the chat as URGENT and instantly alert admins (in-app + email).</p>
            </div>
            <Switch checked={form.emergency_alerts_enabled} onCheckedChange={(v) => setForm({ ...form, emergency_alerts_enabled: v })} data-testid="wa-emergency-switch" />
          </div>
          <div>
            <Label className="text-xs">Keywords (comma-separated)</Label>
            <Textarea className="mt-1" rows={3} value={form.emergency_keywords}
                      onChange={(e) => setForm({ ...form, emergency_keywords: e.target.value })}
                      placeholder="leak, flood, no heating, gas smell, fire…" data-testid="wa-emergency-keywords" />
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Paste these into Meta's Webhook configuration</p>
          <CopyRow label="Callback URL" value={cfg?.webhook_url || ""} testid="wa-webhook-url" />
          <CopyRow label="Verify Token" value={cfg?.verify_token || ""} testid="wa-verify-token" />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="wa-test-connection-btn">
            <ArrowClockwise className={`h-4 w-4 mr-1 ${testing ? "animate-spin" : ""}`} /> Test Connection
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-save-settings-btn">
            {saving ? "Saving…" : "Save Credentials"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== Template picker dialog ==================== */
function TemplateDialog({ open, onOpenChange, waId, onSent }) {
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelected(null); setParams([]); setLoadErr(""); setTemplates(null);
    API.get("/whatsapp/templates")
      .then(({ data }) => setTemplates(data))
      .catch((e) => setLoadErr(e?.response?.data?.detail || "Could not load templates"));
  }, [open]);

  const pick = (t) => {
    setSelected(t);
    setParams(Array(t.param_count).fill(""));
  };

  const send = async () => {
    setSending(true);
    try {
      await API.post(`/whatsapp/conversations/${waId}/send-template`, {
        name: selected.name, language: selected.language, body_params: params,
      });
      toast.success("Template sent");
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send template");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="wa-template-dialog">
        <DialogHeader>
          <DialogTitle>Send an approved template</DialogTitle>
          <DialogDescription>Outside the 24-hour window, WhatsApp only allows Meta-approved templates.</DialogDescription>
        </DialogHeader>
        {loadErr && <p className="text-sm text-red-500">{loadErr}</p>}
        {!templates && !loadErr && <p className="text-sm text-muted-foreground">Loading templates…</p>}
        {templates && !selected && (
          <div className="max-h-72 overflow-y-auto divide-y divide-border border border-border rounded-md">
            {templates.filter((t) => t.status === "APPROVED").map((t) => (
              <button key={`${t.name}-${t.language}`} onClick={() => pick(t)}
                      className="w-full text-left px-3 py-2 hover:bg-muted"
                      data-testid={`wa-template-${t.name}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t.name}</span>
                  <Badge variant="outline" className="text-[10px]">{t.language}</Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{t.body}</p>
              </button>
            ))}
            {templates.filter((t) => t.status === "APPROVED").length === 0 && (
              <p className="p-4 text-xs text-muted-foreground">No approved templates yet — create one in Meta Business Manager → WhatsApp Manager → Message Templates.</p>
            )}
          </div>
        )}
        {selected && (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <p className="text-sm font-medium">{selected.name}</p>
              <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{selected.body}</p>
            </div>
            {params.map((p, i) => (
              <div key={i}>
                <Label className="text-xs">Value for {"{{" + (i + 1) + "}}"}</Label>
                <Input className="mt-1" value={p} data-testid={`wa-template-param-${i}`}
                       onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))} />
              </div>
            ))}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setSelected(null)}>Back</Button>
              <Button onClick={send} disabled={sending || params.some((p) => !p.trim() && selected.param_count > 0)}
                      className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-template-send">
                {sending ? "Sending…" : "Send Template"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ==================== Link contact dialog ==================== */
function LinkContactDialog({ open, onOpenChange, waId, linked, onChanged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => { if (!open) { setQ(""); setResults([]); } }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      API.get(`/whatsapp/link-options?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => setResults(data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const doLink = async (r) => {
    try {
      await API.post(`/whatsapp/conversations/${waId}/link`, { type: r.type, id: r.id, client_id: r.client_id });
      toast.success(`Linked to ${r.name}`);
      onOpenChange(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link"); }
  };

  const unlink = async () => {
    try {
      await API.delete(`/whatsapp/conversations/${waId}/link`);
      toast.success("Contact unlinked");
      onOpenChange(false);
      onChanged();
    } catch { toast.error("Could not unlink"); }
  };

  const typeLabel = { client: "Client", client_contact: "Client contact", supplier: "Supplier" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="wa-link-dialog">
        <DialogHeader>
          <DialogTitle>Link to a known contact</DialogTitle>
          <DialogDescription>Search your clients, client contacts and suppliers.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Search by name or company…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wa-link-search" />
        <div className="max-h-60 overflow-y-auto divide-y divide-border border border-border rounded-md">
          {results.map((r, i) => (
            <button key={`${r.type}-${r.id}-${i}`} onClick={() => doLink(r)}
                    className="w-full text-left px-3 py-2 hover:bg-muted" data-testid={`wa-link-option-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{r.name || r.company}</span>
                <Badge variant="outline" className="text-[10px] shrink-0">{typeLabel[r.type]}</Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">{[r.company, r.phone].filter(Boolean).join(" · ")}</p>
            </button>
          ))}
          {q.trim().length >= 2 && results.length === 0 && <p className="p-4 text-xs text-muted-foreground">No matches.</p>}
          {q.trim().length < 2 && <p className="p-4 text-xs text-muted-foreground">Type at least 2 characters to search.</p>}
        </div>
        {linked && (
          <Button variant="outline" className="text-red-600" onClick={unlink} data-testid="wa-unlink-btn">
            Unlink current contact ({linked.linked_name})
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ==================== Contact history dialog ==================== */
function ContextDialog({ open, onOpenChange, waId }) {
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    if (!open) return;
    setCtx(null);
    API.get(`/whatsapp/conversations/${waId}/context`).then(({ data }) => setCtx(data)).catch(() => {});
  }, [open, waId]);

  const Row = ({ item, to }) => (
    <Link to={to} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted border-b border-border last:border-0">
      <span className="text-sm truncate">{item.reference ? `${item.reference} — ` : ""}{item.title}</span>
      <Badge variant="outline" className="text-[10px] shrink-0">{item.status}</Badge>
    </Link>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="wa-context-dialog">
        <DialogHeader>
          <DialogTitle>Contact history</DialogTitle>
          {ctx?.linked && (
            <DialogDescription>
              {ctx.linked.linked_name}{ctx.linked.linked_company ? ` · ${ctx.linked.linked_company}` : ""}
              {ctx.linked.auto_linked ? " (auto-matched by phone)" : ""}
            </DialogDescription>
          )}
        </DialogHeader>
        {!ctx ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-4">
            {!ctx.linked && <p className="text-sm text-muted-foreground">Not linked to a known contact yet — use "Link contact" to connect this chat to a client or supplier.</p>}
            {(ctx.created_requests || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Created from this chat</p>
                <div className="border border-border rounded-md">
                  {ctx.created_requests.map((r) => <Row key={r.id} item={r} to={`/job-requests/${r.id}`} />)}
                </div>
              </div>
            )}
            {(ctx.job_requests || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Recent job requests</p>
                <div className="border border-border rounded-md" data-testid="wa-context-jrs">
                  {ctx.job_requests.map((r) => <Row key={r.id} item={r} to={`/job-requests/${r.id}`} />)}
                </div>
              </div>
            )}
            {(ctx.jobs || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Recent jobs</p>
                <div className="border border-border rounded-md" data-testid="wa-context-jobs">
                  {ctx.jobs.map((r) => <Row key={r.id} item={r} to={`/jobs/${r.id}`} />)}
                </div>
              </div>
            )}
            {ctx.linked && (ctx.job_requests || []).length === 0 && (ctx.jobs || []).length === 0 && (ctx.created_requests || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No jobs or requests on record yet.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ==================== Job from chat dialog ==================== */
function JobFromChatDialog({ open, onOpenChange, convo, messages }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [address, setAddress] = useState("");
  const [checked, setChecked] = useState({});
  const [creating, setCreating] = useState(false);

  const mediaMsgs = messages.filter((m) => m.media && m.direction === "in");

  useEffect(() => {
    if (!open || !convo) return;
    const display = convo.linked_name || convo.name || `+${convo.wa_id}`;
    setTitle(`WhatsApp request from ${display}`);
    const texts = messages.filter((m) => m.direction === "in" && m.text && !m.media).slice(-10).map((m) => m.text);
    setDetails([`From WhatsApp conversation with ${display} (+${convo.wa_id}):`, "", ...texts.map((t) => `• ${t}`)].join("\n"));
    setAddress("");
    const med = {};
    messages.filter((m) => m.media && m.direction === "in").forEach((m) => { med[m.wamid] = true; });
    setChecked(med);
    // eslint-disable-next-line
  }, [open]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await API.post(`/whatsapp/conversations/${convo.wa_id}/create-job-request`, {
        title, request_details: details, property_address: address,
        message_ids: Object.keys(checked).filter((k) => checked[k]),
      });
      toast.success(`Job request ${data.reference} created${data.attachments_carried ? ` with ${data.attachments_carried} attachment(s)` : ""}`);
      onOpenChange(false);
      navigate(`/job-requests/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create job request");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wa-jr-dialog">
        <DialogHeader>
          <DialogTitle>Create Job Request from this chat</DialogTitle>
          <DialogDescription>The message text and selected photos/files will be carried across.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="wa-jr-title" />
          </div>
          <div>
            <Label className="text-xs">Request details</Label>
            <Textarea className="mt-1" rows={6} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="wa-jr-details" />
          </div>
          <div>
            <Label className="text-xs">Property address (optional)</Label>
            <Input className="mt-1" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. 12 High Street, Maidstone, Kent" data-testid="wa-jr-address" />
          </div>
          {mediaMsgs.length > 0 && (
            <div>
              <Label className="text-xs">Attach photos / files from the chat</Label>
              <div className="mt-1 border border-border rounded-md divide-y divide-border max-h-40 overflow-y-auto">
                {mediaMsgs.map((m) => (
                  <label key={m.wamid} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                    <Checkbox checked={!!checked[m.wamid]} onCheckedChange={(v) => setChecked({ ...checked, [m.wamid]: !!v })}
                              data-testid={`wa-jr-media-${m.wamid}`} />
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{m.media?.filename || m.text || `[${m.type}]`}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Note: WhatsApp media older than ~7 days may no longer be downloadable.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={creating || !title.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-jr-create">
            {creating ? "Creating…" : "Create Job Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== Main page ==================== */
export default function WhatsAppInboxPage() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [convos, setConvos] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newChat, setNewChat] = useState({ phone: "", name: "" });
  const [showLink, setShowLink] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const canCreateJr = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const msgEndRef = useRef(null);
  const fileRef = useRef(null);
  const msgCountRef = useRef(0);

  const fetchStatus = useCallback(() => {
    API.get("/whatsapp/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const fetchConvos = useCallback((query = "") => {
    API.get(`/whatsapp/conversations${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then(({ data }) => setConvos(data)).catch(() => {});
  }, []);

  const fetchMessages = useCallback(async (waId, silent = false) => {
    try {
      const { data } = await API.get(`/whatsapp/conversations/${waId}/messages`);
      setMessages(data);
      if (!silent || data.length !== msgCountRef.current) {
        setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: silent ? "smooth" : "auto" }), 60);
      }
      msgCountRef.current = data.length;
    } catch {}
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchConvos();
    const iv = setInterval(() => fetchConvos(q), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!active) return;
    msgCountRef.current = 0;
    fetchMessages(active.wa_id);
    const iv = setInterval(() => fetchMessages(active.wa_id, true), 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [active?.wa_id]);

  useEffect(() => {
    const t = setTimeout(() => fetchConvos(q), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const activeConvo = convos.find((c) => c.wa_id === active?.wa_id) || active;

  const send = async () => {
    if (!body.trim() || !active) return;
    setSending(true);
    try {
      await API.post(`/whatsapp/conversations/${active.wa_id}/send`, { body: body.trim() });
      setBody("");
      fetchMessages(active.wa_id);
      fetchConvos(q);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send";
      if (e?.response?.status === 409) {
        toast.error(detail);
        setShowTemplates(true);
      } else toast.error(detail);
    } finally { setSending(false); }
  };

  const sendFile = async (file) => {
    if (!file || !active) return;
    const fd = new FormData();
    fd.append("file", file);
    setSending(true);
    try {
      await API.post(`/whatsapp/conversations/${active.wa_id}/send-media`, fd);
      fetchMessages(active.wa_id);
      fetchConvos(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send file");
    } finally { setSending(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const createChat = async () => {
    try {
      const { data } = await API.post("/whatsapp/conversations", newChat);
      setShowNew(false);
      setNewChat({ phone: "", name: "" });
      fetchConvos(q);
      setActive(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start chat");
    }
  };

  const assign = async () => {
    if (!active) return;
    const mine = activeConvo?.assigned_name === user?.name;
    try {
      await API.post(`/whatsapp/conversations/${active.wa_id}/assign`, { to_me: !mine });
      fetchConvos(q);
    } catch {}
  };

  const clearUrgent = async () => {
    if (!active) return;
    try {
      await API.post(`/whatsapp/conversations/${active.wa_id}/clear-urgent`);
      fetchConvos(q);
      toast.success("Marked as handled");
    } catch {}
  };

  const downloadMedia = async (m) => {
    try {
      const r = await API.get(`/whatsapp/media/${m.media.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m.media.filename || "whatsapp-file";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Media unavailable (WhatsApp media expires after ~7 days)");
    }
  };

  const windowOpen = activeConvo?.window_open;

  return (
    <div className="space-y-4" data-testid="wa-inbox-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-emerald-600 flex items-center justify-center">
            <WhatsappLogo className="h-6 w-6 text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl font-bold">WhatsApp Inbox</h1>
            {status?.connected ? (
              <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-0" data-testid="wa-status-badge">
                ● {status.verified_name || "Connected"} {status.phone_display && `· ${status.phone_display}`}
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-0" data-testid="wa-status-badge">
                Not connected
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowNew(true)} data-testid="wa-new-chat-btn">
            <Plus className="h-4 w-4 mr-1" /> New Chat
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="wa-settings-btn">
              <Gear className="h-4 w-4 mr-1" /> Setup
            </Button>
          )}
        </div>
      </div>

      {status && !status.connected && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 flex flex-wrap items-center justify-between gap-2" data-testid="wa-setup-banner">
          <span>
            <strong>WhatsApp isn't connected yet.</strong>{" "}
            {isAdmin ? "Add your Meta Cloud API credentials to start sending and receiving messages." : "Ask an admin to connect the WhatsApp Business account."}
          </span>
          {isAdmin && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowSettings(true)} data-testid="wa-setup-banner-btn">
              Connect WhatsApp
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-3 h-[calc(100vh-240px)] min-h-[440px]">
        {/* Conversation list */}
        <div className="border border-border rounded-md flex flex-col overflow-hidden bg-card">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <MagnifyingGlass className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8 h-9" placeholder="Search name or number…" value={q}
                     onChange={(e) => setQ(e.target.value)} data-testid="wa-search-input" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {convos.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                No conversations yet.<br />Incoming WhatsApp messages will appear here, or start a chat with "New Chat".
              </div>
            ) : convos.map((c) => (
              <button key={c.wa_id} onClick={() => setActive(c)}
                      className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted ${active?.wa_id === c.wa_id ? "bg-muted" : ""}`}
                      data-testid={`wa-convo-${c.wa_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{c.name || `+${c.wa_id}`}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.urgent && <Badge className="text-[9px] h-4 px-1 bg-red-600 text-white border-0" data-testid={`wa-urgent-badge-${c.wa_id}`}>URGENT</Badge>}
                    {c.window_open && <span className="h-2 w-2 rounded-full bg-emerald-500" title="24h reply window open" />}
                    {c.unread_count > 0 && <Badge className="text-[10px] h-4 min-w-4 px-1 bg-emerald-600">{c.unread_count}</Badge>}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {c.last_direction === "out" ? "You: " : ""}{c.last_message_preview || "(no messages)"}
                </p>
                <div className="flex items-center justify-between mt-0.5">
                  <p className="text-[10px] text-muted-foreground/60">{timeAgo(c.last_message_at)}</p>
                  {c.assigned_name && <p className="text-[10px] text-emerald-600 truncate">→ {c.assigned_name}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="border border-border rounded-md flex flex-col overflow-hidden bg-card" data-testid="wa-thread">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
              <WhatsappLogo className="h-12 w-12 opacity-25" weight="fill" />
              <p className="text-sm">Select a conversation to start messaging</p>
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-border flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <UserCircle className="h-8 w-8 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{activeConvo?.name || `+${active.wa_id}`}</p>
                    <p className="text-xs text-muted-foreground">+{active.wa_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {windowOpen ? (
                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-0 text-[10px]" data-testid="wa-window-chip">
                      Reply window open
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-0 text-[10px]" data-testid="wa-window-chip">
                      24h window closed
                    </Badge>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={assign} data-testid="wa-assign-btn">
                    {activeConvo?.assigned_name === user?.name ? "Unassign" : "Assign to me"}
                  </Button>
                </div>
              </div>

              <div className="px-3 py-1.5 border-b border-border bg-muted/40 flex flex-wrap items-center justify-between gap-2 text-xs" data-testid="wa-linked-bar">
                {activeConvo?.linked_type ? (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <LinkSimple className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span className="truncate">
                      <strong>{activeConvo.linked_name}</strong>
                      {activeConvo.linked_company && activeConvo.linked_company !== activeConvo.linked_name && ` · ${activeConvo.linked_company}`}
                    </span>
                    <Badge variant="outline" className="text-[9px] shrink-0">
                      {activeConvo.linked_type === "supplier" ? "Supplier" : activeConvo.linked_type === "client_contact" ? "Client contact" : "Client"}
                    </Badge>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Not linked to a known contact</span>
                )}
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setShowContext(true)} data-testid="wa-history-btn">
                    <ClockCounterClockwise className="h-3.5 w-3.5 mr-1" /> History
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setShowLink(true)} data-testid="wa-link-btn">
                    <LinkSimple className="h-3.5 w-3.5 mr-1" /> {activeConvo?.linked_type ? "Re-link" : "Link contact"}
                  </Button>
                  {canCreateJr && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-emerald-700 dark:text-emerald-400" onClick={() => setShowJobDialog(true)} data-testid="wa-create-jr-btn">
                      <ClipboardText className="h-3.5 w-3.5 mr-1" /> New Job Request
                    </Button>
                  )}
                </div>
              </div>

              {activeConvo?.urgent && (
                <div className="px-3 py-1.5 border-b border-red-300 bg-red-50 dark:bg-red-950/30 flex items-center justify-between gap-2 text-xs text-red-700 dark:text-red-300" data-testid="wa-urgent-banner">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Warning className="h-4 w-4 shrink-0" weight="fill" />
                    <span className="truncate"><strong>Urgent keywords detected:</strong> {(activeConvo.urgent_keywords || []).join(", ")}</span>
                  </span>
                  <Button size="sm" variant="outline" className="h-6 text-xs border-red-300 text-red-700 dark:text-red-300 shrink-0"
                          onClick={clearUrgent} data-testid="wa-clear-urgent-btn">
                    Mark handled
                  </Button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-muted/30">
                {messages.map((m) => (
                  <div key={m.wamid} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                      m.direction === "out" ? "bg-emerald-600 text-white rounded-br-none" : `bg-card border ${m.urgent ? "border-red-400 border-2" : "border-border"} rounded-bl-none`
                    }`}>
                      {m.direction === "out" && m.agent_name && (
                        <p className="text-[10px] font-semibold text-white/80 mb-0.5">{m.agent_name}</p>
                      )}
                      {m.media && m.type === "image" ? (
                        <div className="space-y-1">
                          <MediaImage mediaId={m.media.id} />
                          {m.media.caption && <p>{m.media.caption}</p>}
                        </div>
                      ) : m.media ? (
                        <button onClick={() => downloadMedia(m)}
                                className={`flex items-center gap-2 underline ${m.direction === "out" ? "text-white" : "text-emerald-700 dark:text-emerald-400"}`}>
                          <FileText className="h-4 w-4" /> {m.media.filename || m.text || `[${m.type}]`}
                        </button>
                      ) : (
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                      )}
                      <div className={`flex items-center gap-1 justify-end mt-0.5 ${m.direction === "out" ? "text-white/60" : "text-muted-foreground/70"}`}>
                        <span className="text-[10px]">{fmtTime(m.created_at)}</span>
                        {m.direction === "out" && <StatusTicks status={m.status} />}
                      </div>
                      {m.status === "failed" && m.error && (
                        <p className="text-[10px] text-red-200 mt-0.5">Failed: {m.error?.title || m.error?.message || "delivery error"}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={msgEndRef} />
              </div>

              <div className="border-t border-border p-2">
                {!windowOpen && (
                  <div className="mb-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300 flex items-center justify-between gap-2">
                    <span>WhatsApp's 24-hour reply window is closed — you can only send an approved template until they message you again.</span>
                    <Button size="sm" variant="outline" className="h-6 text-xs shrink-0" onClick={() => setShowTemplates(true)} data-testid="wa-window-template-btn">
                      Send template
                    </Button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input type="file" ref={fileRef} className="hidden" onChange={(e) => sendFile(e.target.files?.[0])}
                         accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" />
                  <Button size="icon" variant="ghost" disabled={!windowOpen || sending} onClick={() => fileRef.current?.click()}
                          title="Attach file" data-testid="wa-attach-btn">
                    <Paperclip className="h-5 w-5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setShowTemplates(true)} title="Send approved template" data-testid="wa-template-btn">
                    <Article className="h-5 w-5" />
                  </Button>
                  <Input value={body} onChange={(e) => setBody(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                         placeholder={windowOpen ? "Type a message…" : "Free-text disabled — window closed"}
                         disabled={!windowOpen || sending} data-testid="wa-message-input" />
                  <Button onClick={send} disabled={!windowOpen || sending || !body.trim()}
                          className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-send-btn">
                    <PaperPlaneTilt className="h-4 w-4" weight="fill" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <SettingsDialog open={showSettings} onOpenChange={setShowSettings} onConnected={fetchStatus} />
      {active && (
        <TemplateDialog open={showTemplates} onOpenChange={setShowTemplates} waId={active.wa_id}
                        onSent={() => { fetchMessages(active.wa_id); fetchConvos(q); }} />
      )}
      {active && (
        <LinkContactDialog open={showLink} onOpenChange={setShowLink} waId={active.wa_id}
                           linked={activeConvo?.linked_type ? activeConvo : null} onChanged={() => fetchConvos(q)} />
      )}
      {active && <ContextDialog open={showContext} onOpenChange={setShowContext} waId={active.wa_id} />}
      {active && (
        <JobFromChatDialog open={showJobDialog} onOpenChange={setShowJobDialog} convo={activeConvo} messages={messages} />
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-sm" data-testid="wa-new-chat-dialog">
          <DialogHeader>
            <DialogTitle>Start a new WhatsApp chat</DialogTitle>
            <DialogDescription>Enter the contact's phone number. Note: to message someone who hasn't messaged you first, you must use an approved template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Phone number</Label>
              <Input className="mt-1" placeholder="07123 456789 or +447123456789" value={newChat.phone}
                     onChange={(e) => setNewChat({ ...newChat, phone: e.target.value })} data-testid="wa-new-chat-phone" />
            </div>
            <div>
              <Label className="text-xs">Name (optional)</Label>
              <Input className="mt-1" placeholder="e.g. John Smith — Tenant, Flat 4" value={newChat.name}
                     onChange={(e) => setNewChat({ ...newChat, name: e.target.value })} data-testid="wa-new-chat-name" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createChat} disabled={!newChat.phone.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-new-chat-create">
              Start Chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
